"use client";

import Image from "next/image";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { GithubIcon, GlobeIcon, GraduationCap, Linkedin, TwitterIcon } from "lucide-react";

interface Person {
  name: string;
  image: string;
  link: string;
  title: string;
  designation: string;
  linkedIn?: string;
  twitter?: string;
  github?: string;
  personalWebsite?: string;
  googleScholar?: string;
}

interface PeopleCardProps {
  person: Person;
  featured?: boolean;
}

export default function PeopleCard({ person, featured = false }: PeopleCardProps) {
  // Fixed pixel sizes instead of `fill` + aspect-ratio wrappers — this is what
  // actually controls how big the photo renders. featured cards get a slightly
  // larger avatar than regular grid cards.
  const avatarSize = featured ? 200 : 164;

  return (
    <div className="group cursor-default h-full">
      <Card
        className={`relative h-full flex flex-col overflow-hidden hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 mx-auto gap-0 py-0 w-[290px] ${
          featured ? "w-[340px]" : ""
        }`}
      >
        {/* soft color glow behind the photo, matching the Faculty Advisor card */}
        <div
          className="absolute left-1/2 top-6 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl pointer-events-none"
          style={{ width: avatarSize * 1.5, height: avatarSize * 1.5 }}
        />
        <div
          className="absolute left-1/2 top-6 -translate-x-1/2 rounded-full bg-primary/30 blur-2xl pointer-events-none"
          style={{ width: avatarSize * 0.9, height: avatarSize * 0.9 }}
        />

        <div className="relative flex justify-center pt-6">
          <div
            className="relative rounded-full overflow-hidden ring-1 ring-border"
            style={{ width: avatarSize, height: avatarSize }}
          >
            <Image
              src={person.image}
              alt={person.name}
              fill
              sizes={`${avatarSize}px`}
              className="object-cover object-top transition-transform duration-300 group-hover:scale-105"
              onError={(e) => {
                e.currentTarget.src = "/images/placeholder-avatar.svg";
              }}
            />
          </div>
        </div>

        <CardHeader className="relative text-center px-4 pb-0 pt-4 gap-1">
          <CardTitle
            className={`group-hover:text-primary transition-colors duration-200 leading-tight whitespace-nowrap ${
              featured ? "text-xl" : "text-base"
            }`}
          >
            {person.name}
          </CardTitle>
        </CardHeader>

        {person.designation && (
          <CardContent className="relative text-center px-4 pt-1.5 pb-0">
            <p className={`text-gray-600 leading-snug ${featured ? "text-sm" : "text-sm"}`}>
              {person.designation}
            </p>
          </CardContent>
        )}

        <CardFooter className="relative flex justify-center gap-3 px-4 pt-3 pb-5 mt-auto">
          {person.linkedIn && (
            <a href={person.linkedIn} target="_blank" rel="noopener noreferrer">
              <Linkedin className="text-primary hover:scale-110 transition-transform" size={17} />
            </a>
          )}
          {person.twitter && (
            <a href={person.twitter} target="_blank" rel="noopener noreferrer">
              <TwitterIcon className="text-primary hover:scale-110 transition-transform" size={17} />
            </a>
          )}
          {person.github && (
            <a href={person.github} target="_blank" rel="noopener noreferrer">
              <GithubIcon className="text-primary hover:scale-110 transition-transform" size={17} />
            </a>
          )}
          {person.personalWebsite && (
            <a href={person.personalWebsite} target="_blank" rel="noopener noreferrer">
              <GlobeIcon className="text-primary hover:scale-110 transition-transform" size={17} />
            </a>
          )}
          {person.googleScholar && (
            <a href={person.googleScholar} target="_blank" rel="noopener noreferrer">
              <GraduationCap className="text-primary hover:scale-110 transition-transform" size={17} />
            </a>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
